/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Empty } from 'antd';
import Tree, { mutateTree, moveItemOnTree } from '@atlaskit/tree';

import { deleteItemFromTree } from '@utils/tree';
import { useModalControls } from '@hooks';
import InfoItem from '@molecules/InfoItem';
import { Button, Sortable } from '@atoms';

import NameFormModal from './NameFormModal';
import s from './styles.module.less';

const MenuTree = ({ value, tree = value, onChange }) => {
  const treeRef = React.useRef();
  const [height, setHeight] = React.useState();
  const [selectedMenuItem, setSelectedMenuItem] = React.useState(null);
  const modalControls = useModalControls(false);

  // little hack to remove page jumping when dragging item with nested items
  React.useEffect(() => {
    setHeight(treeRef.current?.containerElement?.clientHeight);
  }, [tree]);

  const openEditMenuItemModal = React.useCallback(
    (item) => {
      setSelectedMenuItem(item);
      modalControls.show();
    },
    [modalControls],
  );

  const onEditMenuItemCancel = React.useCallback(() => {
    setSelectedMenuItem(null);
    modalControls.hide();
  }, [modalControls]);

  const handleRemove = React.useCallback(
    (item) => {
      const newTree = deleteItemFromTree(item, tree);
      // eslint-disable-next-line no-unused-expressions
      onChange?.(newTree);
    },
    [onChange, tree],
  );

  const handleDragEnd = React.useCallback(
    (src, dest) => {
      if (!dest || (dest?.parentId === src?.parentId && dest?.index === src?.index)) {
        return;
      }

      let newTree = moveItemOnTree(tree, src, dest);
      newTree = mutateTree(newTree, dest.parentId, { isExpanded: true });
      // eslint-disable-next-line no-unused-expressions
      onChange?.(newTree);
    },
    [onChange, tree],
  );

  const handleNameFormSubmit = React.useCallback(
    (values) => {
      const newTree = mutateTree(tree, selectedMenuItem.id, {
        data: { ...tree.items[selectedMenuItem.id].data, title: values.title },
      });

      // eslint-disable-next-line no-unused-expressions
      onChange?.(newTree);
      modalControls.hide();
    },
    [modalControls, onChange, selectedMenuItem, tree],
  );

  if (!value?.items[value?.rootId].hasChildren) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  const renderItem = ({ item, provided, ...rest }) => (
    <Sortable.ItemWithDelete
      as={Sortable.TreeItem}
      onDelete={() => handleRemove(item)}
      componentBackground
      {...provided}
    >
      <InfoItem description={item.data?.linkUrl}>
        <Button type="link" onClick={() => openEditMenuItemModal(item)} className={s.editTrigger}>
          {item.data.title}
        </Button>
      </InfoItem>
    </Sortable.ItemWithDelete>
  );

  return (
    <div style={{ height, margin: '-5px 0' }}>
      <Tree
        ref={treeRef}
        tree={tree}
        renderItem={renderItem}
        onDragEnd={handleDragEnd}
        offsetPerLevel={20}
        isDragEnabled
        isNestingEnabled
      />

      <NameFormModal
        visible={modalControls.visible}
        onCancel={onEditMenuItemCancel}
        onSubmit={handleNameFormSubmit}
        menuItem={selectedMenuItem}
      />
    </div>
  );
};

export default MenuTree;
